import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import {
  CardsListItem,
  ListCardsResponseWithProjectName,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { useUserUidNumber } from "../auth/useUserUidNumber.ts";
import { resolveEmployeeName } from "./getSenderSignature.ts";
import { queryKeys } from "./queryKeys.ts";

export const useToggleCardSignatureCache = () => {
  const queryClient = useQueryClient();
  const userUidNumber = useUserUidNumber();

  return useCallback(
    (
      cardId: string | undefined,
      projectId: string,
      isCurrentlySigned: boolean,
    ) => {
      queryClient.setQueryData<ListCardsResponseWithProjectName>(
        queryKeys.cardsInProject(projectId),
        (old) => {
          const oldCard = old?.items.find((card) => card.id === cardId);
          if (!oldCard) return old;
          const updatedSignatures = isCurrentlySigned
            ? addUserToSignatures(
                oldCard.signatures,
                userUidNumber,
                userUidNumber === undefined
                  ? undefined
                  : resolveEmployeeName(queryClient, userUidNumber),
              )
            : removeUserFromSignatures(oldCard.signatures, userUidNumber);
          const tempCard: CardsListItem = {
            ...oldCard,
            isSignedByUser: isCurrentlySigned,
            ...(updatedSignatures !== undefined && {
              signatures: updatedSignatures,
              numberOfSignatures: updatedSignatures.length,
            }),
          };

          const updatedResponse: ListCardsResponseWithProjectName = {
            projectDisplayName: old?.projectDisplayName || "",
            items: [
              ...(old?.items.filter((card) => card.id !== tempCard.id) || []),
              tempCard,
            ],
            hiddenFields: old?.hiddenFields || [],
            projectShepherds: old?.projectShepherds || {
              groups: [],
              individuals: [],
            },
            projectInfoBox: old?.projectInfoBox,
            hasPermissions: old?.hasPermissions ?? true,
          };
          return updatedResponse;
        },
      );
    },
    [queryClient, userUidNumber],
  );
};

const addUserToSignatures = (
  signatures: CardsListItem["signatures"],
  userUidNumber: number | undefined,
  userName: string | undefined,
): CardsListItem["signatures"] | undefined => {
  if (signatures === undefined || userUidNumber === undefined) return undefined;
  return signatures.some((s) => s.uidNumber === userUidNumber)
    ? signatures
    : [...signatures, { uidNumber: userUidNumber, name: userName ?? "" }];
};

const removeUserFromSignatures = (
  signatures: CardsListItem["signatures"],
  userUidNumber: number | undefined,
): CardsListItem["signatures"] | undefined => {
  if (signatures === undefined || userUidNumber === undefined) return undefined;
  return signatures.filter((s) => s.uidNumber !== userUidNumber);
};

export const useCancelCardListQueries = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (projectId: string) =>
      queryClient.cancelQueries({
        queryKey: queryKeys.cardsInProject(projectId),
      }),
    [queryClient],
  );
};
